import { useState } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import dayjs, { Dayjs } from 'dayjs';

import { InfoSvg } from '../../../../shared/Assets/svgIcons';
import { PEARL } from '../../../../shared/Helpers/colors';
import { CustomAlert, CustomButton, Picker, Text } from '../../../../shared/uiComponents';
import { Unlocked } from '../../../../shared/Assets/sessionStatuses';
import { dateNormalizer, errorNormalizer } from '../../../../shared/Helpers/functions';
import { useCheckPermission } from '../../../../shared/Helpers/hooks';
import { useResetCalendarData } from '../../Helpers/hooks';
import { CONFIRM_ERROR_MESSAGE_ASSESMENT } from '../../../../shared/Helpers/constants';
import { PERMISSIONS } from '../../../../App/constants';

import { DispatchProperties, useSelector } from '../../../../redux/store';
import { SoapNotesStatuses } from '../../../../redux/API/ClientAPIHelpers/dataCollectionProperties';
import { activateSession, unlockSession } from '../../../../redux/State/clientSlice/sessionSlice';
import { EventDataProperties } from '../../../../redux/API/ClientAPIHelpers/calendarProperties';

const initialExpirationDate = dayjs().add(2, 'days');

const UnlockSession = ({ data, onClose }: { data: EventDataProperties | null; onClose: () => void }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const { resetData } = useResetCalendarData();
	const { permissionGranted } = useCheckPermission(PERMISSIONS.SOAPNOTE.UNLOCK);

	const [openUnlock, setOpenUnlock] = useState<boolean>(false);
	const [confirmOverrideOpen, setConfirmOverrideOpen] = useState(false);
	const [expirationDateValue, setExpirationDateValue] = useState<Dayjs | null>(initialExpirationDate);

	const noteStatus = data?.reportStatus?.status;
	const isNoteExpired = noteStatus === SoapNotesStatuses.expired48 || noteStatus === SoapNotesStatuses.expiredWeek;
	const hasNoNote = noteStatus == null; // The loose equality == null checks for both undefined and null
	// Unlock is tied to SOAP note expiry, so it only applies to session types that produce a note.
	// Data-collection-only types (e.g. ELC) never have a note and must not be offered Unlock (a completed
	// such session would otherwise always look "unlockable").
	const requiresSoapNote = data?.sessionType?.requiresSoapNote ?? true;
	const canUnlock = requiresSoapNote && (hasNoNote || isNoteExpired);

	const toggleUnlockHandler = () => setOpenUnlock(!openUnlock);

	const unlockSessionHandler = (force = false) => {
		if (!data?.id || !expirationDateValue) return;
		const { id } = data;
		dispatch(
			unlockSession({
				eventId: id,
				nextExpirationDate: dateNormalizer(expirationDateValue),
				isAdminForceOperation: force,
			}),
		)
			.then(unwrapResult)
			.then(() => {
				resetData();
				toast('Unlocked');
				onClose();
			})
			.catch((e) => {
				const responseMessage = e?.title || (typeof e === 'string' ? e : '');

				if (!force && responseMessage.includes(CONFIRM_ERROR_MESSAGE_ASSESMENT)) {
					setConfirmOverrideOpen(true);
				} else {
					errorNormalizer(e);
				}
			});
	};

	// Show only if: (No note OR Expired) AND has permission
	if (!permissionGranted || !canUnlock) {
		return null;
	}

	return (
		<>
			<CustomAlert
				open={confirmOverrideOpen}
				title="Confirm override"
				onClose={() => setConfirmOverrideOpen(false)}
				Content={() => (
					<div>
						<Text title={CONFIRM_ERROR_MESSAGE_ASSESMENT} className="marginBottom16" />
						<div className="confirmOverrideErrorMessageAlertActions">
							<CustomButton title="Cancel" buttonType="secondaryGray" onClick={() => setConfirmOverrideOpen(false)} />
							<CustomButton title="Override and submit" onClick={() => {
								setConfirmOverrideOpen(false);
								unlockSessionHandler(true);
							}} />
						</div>
					</div>
				)}
			/>
			{!openUnlock && (
				<div className="actionRow" onClick={toggleUnlockHandler}>
					<div className="actionIcon">
						<Unlocked color={PEARL.BASE[600]} />
					</div>
					<div className="actionContent">
						<Text title="Unlock" type="psm" fontWeight="medium" />
					</div>
				</div>
			)}
			{openUnlock && (
				<div className="actionRow">
					<Picker.CustomDate
						label=""
						value={expirationDateValue}
						onChange={setExpirationDateValue}
						className="flex100"
					/>
					<CustomButton className="flex100" title="Save" onClick={() => unlockSessionHandler()} />
					<CustomButton buttonType="secondaryGray" title="Cancel" onClick={toggleUnlockHandler} />
				</div>
			)}
		</>
	);
};

const ActivateSession = ({ data, onClose }: { data: EventDataProperties | null; onClose: () => void }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const { resetData } = useResetCalendarData();

	const [openActivate, setOpenActivate] = useState<boolean>(false);

	const isSessionCreated = Boolean(data?.sessionId);
	const isNoteSubmitted = data?.reportStatus?.status === SoapNotesStatuses.submitted;
	const requiresSoapNote = data?.sessionType?.requiresSoapNote ?? true;
	const isCompleted = data?.isCompleted === true;

	const toggleActivateHandler = () => setOpenActivate(!openActivate);

	const activateSessionHandler = () => {
		if (!data?.sessionId) return;
		const { sessionId } = data;
		dispatch(activateSession(sessionId))
			.then(unwrapResult)
			.then(() => {
				resetData();
				toast('Activated');
				onClose();
			})
			.catch(errorNormalizer);
	};

	// Show only if: Session exists AND either the note is submitted (clinical types) or the
	// session is completed and doesn't produce a note (data-collection-only types like ELC).
	if (!isSessionCreated || (!isNoteSubmitted && (requiresSoapNote || !isCompleted))) return null;

	return (
		<>
			{!openActivate && (
				<div className="actionRow" onClick={toggleActivateHandler}>
					<div className="actionIcon">
						<Unlocked color={PEARL.BASE[600]} />
					</div>
					<div className="actionContent">
						<Text title="Activate" type="psm" fontWeight="medium" />
					</div>
				</div>
			)}
			{openActivate && (
				<div className="actionRow">
					<Text title="Activate this session?" type="psm" fontWeight="medium" />
					<CustomButton className="flex100" title="Activate" onClick={activateSessionHandler} />
					<CustomButton buttonType="secondaryGray" title="Cancel" onClick={toggleActivateHandler} />
				</div>
			)}
		</>
	);
};

const AdminActions = () => {
	const [openActions, setOpenActions] = useState<boolean>(false);

	const data = useSelector((state) => state.calendarHelper.infoEventData);

	const toggleAdminActionsHandler = () => setOpenActions(!openActions);
	const closeActionsHandler = () => setOpenActions(false);

	return (
		<div className="icon">
			<InfoSvg onClick={toggleAdminActionsHandler} />
			<div style={{ display: openActions ? 'flex' : 'none' }} className="adminActions">
				<UnlockSession data={data} onClose={closeActionsHandler} />
				<ActivateSession data={data} onClose={closeActionsHandler} />
			</div>
		</div>
	);
};

export default AdminActions;
